// returns an object with only the specified key/value pairs kept
export function pick<T>(object: T, ...keys: Array<keyof T>): Partial<T> {
  return object && typeof object === 'object'
    ? keys.reduce((sum, key) => {
        sum[key] = object[key];
        return sum;
      }, {} as Partial<T>)
    : object;
}

// returns an object with the values that pass the given predicate omittied
export function omitBy<T>(
  object: T,
  predicate: (value: T[keyof T]) => boolean,
): Partial<T> {
  return object && typeof object === 'object'
    ? Object.keys(object).reduce((sum, key) => {
        if (!predicate(object[key])) sum[key] = object[key];
        return sum;
      }, {})
    : object;
}

export function isEmpty(
  object: object | null | undefined,
): object is null | undefined {
  return (
    !object || typeof object !== 'object' || Object.keys(object).length === 0
  );
}

export function forEach<K extends string = string, V = any>(
  object: Partial<Record<K, V>>,
  callback: (key: K, value: V) => any,
) {
  Object.keys(object).forEach(key => callback(key as any, object[key]));
}

export function get(obj: object, path: string, fallback?: any) {
  if (!obj || !path) return fallback;

  const paths = Array.isArray(path) ? path : path.split('.');
  let results = obj;
  let i = 0;

  while (i < paths.length && results != null) {
    results = results[paths[i]];
    i++;
  }

  if (i === paths.length) {
    return results !== undefined ? results : fallback;
  }

  return results != null ? results : fallback;
}

export function set(object: object, path: string, value: any) {
  const [head, ...rest] = path.split('.');

  if (rest.length) {
    if (!object[head]) {
      // if something at this path does not yet exist, create it
      // if the next head is not a number, that means it should be an object
      // else if it is a number, that means it should be an array
      object[head] = isNaN(+rest[0]) ? {} : [];
    }
    set(object[head], rest.join('.'), value);
  } else {
    object[head] = value;
  }
}

// https://www.w3resource.com/javascript-exercises/fundamental/javascript-fundamental-exercise-233.php
/**
 * flattens an object
 * e.g. { owner_address_zip: 32801, meta: { ownership_percentage: 0 } }
 * becomes { owner_address_zip: 32801, meta.ownership_percentage: 0 }
 */
export function flatten<T extends object>(obj: T, prefix = '') {
  return Object.keys(obj).reduce((acc, k) => {
    const pre = prefix.length ? prefix + '.' : '';
    if (typeof obj[k] === 'object') {
      Object.assign(acc, flatten(obj[k], pre + k));
    } else {
      acc[pre + k] = obj[k];
    }

    return acc;
  }, {} as { [key: string]: any });
}
